import { Card, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Icon, TextField } from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { useMaterialUIController } from "context";
import PropTypes from "prop-types";
import { lazy, Suspense, useCallback, useEffect, useState } from "react";
import { get, postNoResponse } from "utils/WebService";
import AnexosSolicitacao from "./AnexosSolicitacao";
import { alvoSolicitacao, avisarSolicitacoesAlteradas, comAlfa, listaDe, tipoSolicitacao } from "./solicitacaoUtils";
import { ESCURO } from "assets/coresTema";

// A ficha só carrega se o usuário pedir para abrir (o diálogo vive no cabeçalho de todas as telas)
const BaseDeDadosCreateEdit = lazy(() => import("views/umc/baseDeDados/BaseDeDadosCreateEdit"));

const COR_RECUSA = { light: "#C62828", dark: "#EF5350" };

/**
 * Solicitações (devolução/migração) que o suporte recusou e aguardam a posição do usuário logado:
 * Ciente (encerra) ou Reenviar com nova justificativa (volta para a Fila de Solicitações).
 */
export default function SolicitacoesRecusadasDialog({ open, onClose, onChanged }) {
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;
    const corRecusa = COR_RECUSA[darkMode ? "dark" : "light"];

    const [lista, setLista] = useState([]);
    const [loading, setLoading] = useState(false);
    const [erro, setErro] = useState("");
    const [aviso, setAviso] = useState("");
    // { id, modo: "ciente" | "reenviar" }
    const [resposta, setResposta] = useState(null);
    const [texto, setTexto] = useState("");
    const [enviando, setEnviando] = useState(false);
    const [fichaDevedorId, setFichaDevedorId] = useState(null);

    const carregar = useCallback(() => {
        setLoading(true);
        setErro("");
        get("/solicitacoes-suporte/minhas-recusadas")
            .then(res => setLista(listaDe(res)))
            .catch(err => setErro(err.message || "Erro ao buscar as solicitações recusadas"))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        if (open) {
            setAviso("");
            setResposta(null);
            carregar();
        }
    }, [open, carregar]);

    const responder = (s, modo) => {
        setResposta({ id: s.id, modo });
        setTexto("");
        setErro("");
    };

    const confirmar = async (s) => {
        const reenviar = resposta?.modo === "reenviar";
        if (reenviar && !texto.trim()) {
            setErro("Informe a nova justificativa para reenviar.");
            return;
        }
        setEnviando(true);
        setErro("");
        try {
            if (reenviar) {
                await postNoResponse(`/solicitacoes-suporte/${s.id}/reenviar`, { justificativa: texto.trim() });
            } else {
                await postNoResponse(`/solicitacoes-suporte/${s.id}/ciente`, { comentario: texto.trim() || null });
            }
            setAviso(reenviar
                ? "Solicitação reenviada: ela voltou para a Fila de Solicitações do suporte."
                : "Pronto: a solicitação foi encerrada.");
            setResposta(null);
            const restantes = lista.filter(item => item.id !== s.id);
            setLista(restantes);
            avisarSolicitacoesAlteradas();
            if (onChanged) onChanged();
        } catch (err) {
            setErro(err.message || "Erro ao enviar a sua posição");
        } finally {
            setEnviando(false);
        }
    };

    return (
        <>
            <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
                <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Icon sx={{ color: corRecusa }}>assignment_late</Icon>
                    Solicitações recusadas pelo suporte
                </DialogTitle>
                <DialogContent dividers>
                    <MDTypography variant="caption" color="text" sx={{ display: "block", mb: 2 }}>
                        O suporte recusou estas solicitações e aguarda a sua posição. Se concordar, marque <strong>Ciente</strong> e a solicitação é
                        encerrada. Se não concordar, use <strong>Reenviar</strong> com uma nova justificativa e ela volta para a fila do suporte.
                    </MDTypography>

                    {aviso && (
                        <MDBox mb={2} p={1.5} borderRadius="md" display="flex" alignItems="center" gap={1}
                            sx={{ background: comAlfa("#2E7D32", darkMode ? 0.22 : 0.1) }}>
                            <Icon color="success">check_circle</Icon>
                            <MDTypography variant="button" color="text">{aviso}</MDTypography>
                        </MDBox>
                    )}
                    {erro && (
                        <MDBox mb={2} p={1.5} borderRadius="md" display="flex" alignItems="center" gap={1}
                            sx={{ background: comAlfa("#C62828", darkMode ? 0.22 : 0.1) }}>
                            <Icon color="error">error_outline</Icon>
                            <MDTypography variant="button" color="text">{erro}</MDTypography>
                        </MDBox>
                    )}

                    {loading ? (
                        <MDBox display="flex" justifyContent="center" p={4}><CircularProgress color="info" /></MDBox>
                    ) : lista.length === 0 ? (
                        <MDBox textAlign="center" py={4}>
                            <Icon sx={{ fontSize: "2.2rem !important", color: "#2E7D32" }}>task_alt</Icon>
                            <MDTypography variant="button" color="text" sx={{ display: "block", mt: 1 }}>
                                Nenhuma solicitação aguardando a sua posição.
                            </MDTypography>
                        </MDBox>
                    ) : (
                        <MDBox display="flex" flexDirection="column" gap={2}>
                            {lista.map(s => {
                                const tipo = tipoSolicitacao(s.tipoSolicitacao);
                                const corTipo = darkMode ? tipo.corEscura : tipo.cor;
                                const respondendo = resposta?.id === s.id;
                                return (
                                    <Card key={s.id} variant="outlined" sx={{
                                        p: 2,
                                        background: darkMode ? ESCURO.cartao : "#fafafa",
                                        borderColor: darkMode ? ESCURO.divisoria : "#ddd",
                                        borderLeft: `4px solid ${corRecusa}`,
                                    }}>
                                        <MDBox display="flex" justifyContent="space-between" alignItems="flex-start" gap={1} flexWrap="wrap">
                                            <MDBox minWidth={0}>
                                                <MDBox display="flex" alignItems="center" gap={0.75}>
                                                    <Icon sx={{ color: corTipo, fontSize: "1.1rem !important" }}>{tipo.icone}</Icon>
                                                    <MDTypography variant="button" fontWeight="bold" color="text">{tipo.label}</MDTypography>
                                                </MDBox>
                                                <MDTypography variant="caption" color="text" sx={{ display: "block" }}>
                                                    <strong>{s.nomeDevedor || "-"}</strong>{s.cpfCnpjDevedor ? ` · ${s.cpfCnpjDevedor}` : ""}{s.nomeCliente ? ` · ${s.nomeCliente}` : ""}
                                                </MDTypography>
                                                <MDTypography variant="caption" color="text" sx={{ display: "block", opacity: 0.85 }}>
                                                    {alvoSolicitacao(s)} · solicitada em {s.dataSolicitacao}
                                                </MDTypography>
                                            </MDBox>
                                            {s.qtdCobrancas > 0 && (
                                                <Chip size="small" color="error" icon={<Icon>notifications_active</Icon>}
                                                    label={`Suporte cobrou ${s.qtdCobrancas}× · última ${s.ultimaCobranca}`} sx={{ height: 22, fontSize: 11 }} />
                                            )}
                                        </MDBox>

                                        <MDBox mt={1.5} p={1.5} borderRadius="md" sx={{ background: comAlfa(corRecusa, darkMode ? 0.16 : 0.07), borderLeft: `3px solid ${corRecusa}` }}>
                                            <MDTypography variant="caption" fontWeight="bold" color="text" sx={{ display: "block" }}>
                                                Recusada{s.nomeResponsavel ? ` por ${s.nomeResponsavel}` : ""} em {s.dataResolucao}
                                            </MDTypography>
                                            <MDTypography variant="button" color="text" sx={{ display: "block", whiteSpace: "pre-wrap" }}>{s.motivoRecusa || "-"}</MDTypography>
                                        </MDBox>
                                        {s.observacao && (
                                            <MDTypography variant="caption" color="text" sx={{ display: "block", mt: 1, opacity: 0.85, whiteSpace: "pre-wrap" }}>
                                                <strong>Sua justificativa:</strong> {s.observacao}
                                            </MDTypography>
                                        )}
                                        {/* Anexo errado? Troca, exclui ou adiciona antes de reenviar */}
                                        <MDBox mt={1}>
                                            <AnexosSolicitacao
                                                solicitacao={s}
                                                onChange={(atualizada) => { setErro(""); setLista(l => l.map(x => (x.id === atualizada.id ? atualizada : x))); }}
                                                onErro={setErro}
                                            />
                                        </MDBox>

                                        {respondendo ? (
                                            <MDBox mt={2}>
                                                {resposta.modo === "reenviar" && (
                                                    <MDTypography variant="caption" color="text" sx={{ display: "block", mb: 1, opacity: 0.85 }}>
                                                        Se o anexo estava errado ou faltou documento, troque ou adicione em <strong>Anexos</strong> acima antes de reenviar.
                                                    </MDTypography>
                                                )}
                                                <TextField fullWidth multiline rows={3} variant="outlined" autoFocus value={texto}
                                                    onChange={(e) => setTexto(e.target.value)}
                                                    label={resposta.modo === "reenviar" ? "Nova justificativa (obrigatória)" : "Comentário para o suporte (opcional)"} />
                                                <MDBox display="flex" justifyContent="flex-end" gap={1} mt={1}>
                                                    <MDButton variant="text" color="dark" size="small" onClick={() => setResposta(null)} disabled={enviando}>Voltar</MDButton>
                                                    <MDButton variant="gradient" size="small" color={resposta.modo === "reenviar" ? "info" : "success"}
                                                        onClick={() => confirmar(s)} disabled={enviando || (resposta.modo === "reenviar" && !texto.trim())}>
                                                        {enviando ? <CircularProgress size={16} color="inherit" />
                                                            : resposta.modo === "reenviar" ? "Reenviar para o suporte" : "Confirmar ciência"}
                                                    </MDButton>
                                                </MDBox>
                                            </MDBox>
                                        ) : (
                                            <MDBox display="flex" justifyContent="flex-end" gap={1} mt={2} flexWrap="wrap">
                                                {s.idDevedor && (
                                                    <MDButton variant="text" color="info" size="small" onClick={() => setFichaDevedorId(s.idDevedor)}>
                                                        <Icon sx={{ mr: 0.5 }}>person_search</Icon>Abrir ficha
                                                    </MDButton>
                                                )}
                                                <MDButton variant="outlined" color="info" size="small" onClick={() => responder(s, "reenviar")}>
                                                    <Icon sx={{ mr: 0.5 }}>replay</Icon>Reenviar
                                                </MDButton>
                                                <MDButton variant="gradient" color="success" size="small" onClick={() => responder(s, "ciente")}>
                                                    <Icon sx={{ mr: 0.5 }}>done_all</Icon>Ciente
                                                </MDButton>
                                            </MDBox>
                                        )}
                                    </Card>
                                );
                            })}
                        </MDBox>
                    )}
                </DialogContent>
                <DialogActions>
                    <MDButton onClick={onClose} color="dark" variant="text">Fechar</MDButton>
                </DialogActions>
            </Dialog>

            <Dialog open={!!fichaDevedorId} onClose={() => setFichaDevedorId(null)} fullWidth maxWidth="xl"
                PaperProps={{ style: { backgroundColor: darkMode ? ESCURO.cartao : "#fafafa", minHeight: "80vh" } }}>
                <DialogContent sx={{ p: 1 }}>
                    {fichaDevedorId && (
                        <Suspense fallback={<MDBox display="flex" justifyContent="center" p={5}><CircularProgress color="info" /></MDBox>}>
                            <BaseDeDadosCreateEdit recordId={fichaDevedorId} closeCreateEdit={() => setFichaDevedorId(null)} isViewOnly={true} />
                        </Suspense>
                    )}
                </DialogContent>
            </Dialog>
        </>
    );
}

SolicitacoesRecusadasDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onChanged: PropTypes.func,
};
